import React from 'react'
import { Link } from 'react-router-dom'
import { atcb_action as atcbAction } from 'add-to-calendar-button'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../../common/Icon'
import { Button, ButtonVariant } from '../../common/Button'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useAddToCalendarConfig } from '../../../hooks/useAddToCalendarConfig'
import { PostDocuments } from '../PostDocuments'
import { PostEventAttendance } from './PostEventAttendance'
import { eventDateString, eventTimeString } from './eventDates'
import { PostEventCard } from '../../../generated/api/post-row'

interface PostEventProps {
  event: PostEventCard
  disabled?: boolean
}

const eventUrl = (event: PostEventCard): string =>
  event.kind === 'boardroom'
    ? reverseUrl('management:calendar-detail-information', {
        id: event.societyId,
        eventId: event.id,
      })
    : reverseUrl('society:calendar-detail', {
        id: event.societyId,
        eventId: event.id,
      })

export const PostEvent = ({
  event,
  disabled,
}: PostEventProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const config = useAddToCalendarConfig(event)

  const onAddToCalendar = (): void => {
    atcbAction(config)
  }

  return (
    <div className="flex flex-col gap-4">
      <p style={theme.textVariants.lead} className="break-words">
        {translate('post.event.prefix')}: {event.title}
      </p>
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2">
          <Icon icon={IconChoices.CALENDAR} />
          <p style={theme.textVariants.base}>{eventDateString(event)}</p>
        </div>
        <div className="flex items-start gap-2">
          <Icon icon={IconChoices.CLOCK} />
          <p style={theme.textVariants.base}>
            {eventTimeString(event, translate('post.event.allDay'))}
          </p>
        </div>
        {event.location && (
          <div className="flex items-start gap-2">
            <Icon icon={IconChoices.LOCATION} />
            <p style={theme.textVariants.base}>{event.location}</p>
          </div>
        )}
      </div>
      {!event.cancelled && (
        <>
          <PostDocuments documents={event.documents} disabled={disabled} />
          {event.attendance.enabled && (
            <PostEventAttendance event={event} disabled={disabled} />
          )}
          <div className="flex flex-wrap gap-3">
            <Link to={eventUrl(event)}>
              <Button
                label={translate('post.event.goToEvent')}
                variant={ButtonVariant.PRIMARY}
                disableTabIndex
                disabled={disabled}
              />
            </Link>
            <Button
              label={translate('post.event.addToCalendar')}
              onClick={onAddToCalendar}
              variant={ButtonVariant.DEFAULT}
              icon={IconChoices.CALENDAR}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  )
}
